import BehaviorScope from './analysis/BehaviorScope';
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControlLabel,
  Checkbox,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { useLoginCheck } from "../../hooks/useLoginCheck";

// ダークテーマ（既存ページと統一）
const theme = {
  background: "#111827",
  paper: "#1f2937",
  border: "#374151",
  text: "#f9fafb",
  subText: "#9ca3af",
  grid: "#2d3748",
  tooltip: "rgba(31, 41, 55, 0.97)",
};

// LP比較用のカラーパレット（最大8 LPまで明瞭に区別できる）
const LP_COLORS = [
  "#22d3ee", // シアン
  "#f472b6", // ピンク
  "#facc15", // 黄
  "#34d399", // 緑
  "#a78bfa", // 紫
  "#fb923c", // オレンジ
  "#f87171", // 赤
  "#60a5fa", // 青
];

const cardSx = {
  bgcolor: theme.paper,
  border: `1px solid ${theme.border}`,
  borderRadius: 3,
  boxShadow: "0 4px 24px rgba(0,0,0,0.3)",
  color: theme.text,
};

// 期間プリセット
const PRESETS = [
  { key: "7", label: "直近7日", days: 7 },
  { key: "28", label: "直近28日", days: 28 },
  { key: "90", label: "直近90日", days: 90 },
  { key: "custom", label: "カスタム", days: null },
];

function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return fmtDate(d);
}

// ヒートマップのセル色を青→緑→赤のスケールで返す
function heatColor(value, max) {
  if (!max || value <= 0) return "rgba(255,255,255,0.04)";
  const ratio = Math.min(1, value / max);
  // 0→透明青、0.5→緑、1→赤
  const r = ratio < 0.5 ? Math.round(34 + (250 - 34) * (ratio * 2)) : 250;
  const g = ratio < 0.5
    ? Math.round(211 + (204 - 211) * (ratio * 2))
    : Math.round(204 - (204 - 100) * ((ratio - 0.5) * 2));
  const b = ratio < 0.5
    ? Math.round(238 - (238 - 21) * (ratio * 2))
    : Math.round(21 + (60 - 21) * ((ratio - 0.5) * 2));
  return `rgba(${r},${g},${b},${0.15 + ratio * 0.55})`;
}

// LP×何か の値を取りやすくする
function pivotByPage(rows, valueKey) {
  const map = {};
  rows.forEach((r) => {
    if (!map[r.page]) map[r.page] = {};
    map[r.page][r.key || r.section || ""] = r[valueKey];
  });
  return map;
}

// 期間ピッカー
const PeriodPicker = ({ preset, setPreset, start, end, setStart, setEnd }) => (
  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
    {PRESETS.map((p) => (
      <Box
        key={p.key}
        onClick={() => setPreset(p.key)}
        sx={{
          px: 2,
          py: 0.6,
          borderRadius: 1.5,
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer",
          bgcolor: preset === p.key ? LP_COLORS[0] : theme.border,
          color: preset === p.key ? "#000" : theme.subText,
          "&:hover": { opacity: 0.85 },
          userSelect: "none",
          transition: "all 0.15s",
        }}
      >
        {p.label}
      </Box>
    ))}
    {preset === "custom" && (
      <>
        <TextField
          type="date"
          size="small"
          value={start}
          onChange={(e) => setStart(e.target.value)}
          sx={{
            "& .MuiInputBase-root": { color: theme.text, bgcolor: theme.background },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: theme.border },
          }}
        />
        <Typography sx={{ color: theme.subText, fontSize: 13 }}>〜</Typography>
        <TextField
          type="date"
          size="small"
          value={end}
          onChange={(e) => setEnd(e.target.value)}
          sx={{
            "& .MuiInputBase-root": { color: theme.text, bgcolor: theme.background },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: theme.border },
          }}
        />
      </>
    )}
  </Box>
);

// LP選択
const LpPicker = ({ available, selected, toggle, loading }) => (
  <Card sx={{ ...cardSx, mb: 3 }}>
    <CardContent>
      <Typography sx={{ color: theme.text, fontWeight: 700, mb: 1 }}>
        比較するLP（{selected.length} / {available.length} 選択）
      </Typography>
      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={16} sx={{ color: theme.subText }} />
          <Typography sx={{ color: theme.subText, fontSize: 13 }}>LP一覧を読み込み中...</Typography>
        </Box>
      ) : available.length === 0 ? (
        <Typography sx={{ color: theme.subText, fontSize: 13 }}>
          この期間に section_view が発火したLPはありません
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {available.map((p, i) => {
            const isOn = selected.includes(p.page);
            const color = LP_COLORS[selected.indexOf(p.page) % LP_COLORS.length];
            return (
              <FormControlLabel
                key={p.page}
                control={
                  <Checkbox
                    checked={isOn}
                    onChange={() => toggle(p.page)}
                    sx={{
                      color: theme.subText,
                      "&.Mui-checked": { color: isOn ? color : theme.subText },
                      p: 0.5,
                    }}
                  />
                }
                label={
                  <Typography sx={{ color: theme.text, fontSize: 13 }}>
                    {p.page}{" "}
                    <span style={{ color: theme.subText }}>
                      ({p.total_users}u / {p.event_count}件)
                    </span>
                  </Typography>
                }
                sx={{ m: 0, mr: 2 }}
              />
            );
          })}
        </Box>
      )}
    </CardContent>
  </Card>
);

// KPIカード
const KpiCards = ({ pages, selected, funnel, dwell, cta, aggregates }) => {
  // page → KPI 計算
  const kpis = useMemo(() => {
    return selected.map((page) => {
      const meta = pages.find((p) => p.page === page) || { total_users: 0, event_count: 0 };
      const sections = funnel.filter((r) => r.page === page);
      const maxOrder = sections.length > 0 ? Math.max(...sections.map((s) => s.order)) : 0;
      const lastSection = sections.find((s) => s.order === maxOrder);
      const firstSection = sections.find((s) => s.order === Math.min(...sections.map((s) => s.order)));
      const reachRate =
        firstSection && firstSection.total_users > 0
          ? (lastSection?.total_users || 0) / firstSection.total_users
          : 0;
      const ctaTotal = cta
        .filter((r) => r.page === page)
        .reduce((acc, r) => acc + r.event_count, 0);
      const ctaUsers = cta
        .filter((r) => r.page === page)
        .reduce((acc, r) => acc + r.total_users, 0);
      const ctaRate =
        meta.total_users > 0 ? ctaUsers / meta.total_users : 0;
      const dwellRows = dwell.filter((r) => r.page === page);
      const maxDwell =
        dwellRows.some(r => r.avg_dwell_seconds != null)
          ? Math.max(...dwellRows.filter(r => r.avg_dwell_seconds != null).map(r => r.avg_dwell_seconds))
          : null;
      return {
        page,
        users: meta.total_users,
        reachRate,
        ctaTotal,
        ctaRate,
        maxDwell,
      };
    });
  }, [pages, selected, funnel, dwell, cta, aggregates]);

  if (kpis.length === 0) return null;

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {kpis.map((k, i) => {
        const color = LP_COLORS[i % LP_COLORS.length];
        return (
          <Grid item xs={12} sm={6} md={4} lg={3} key={k.page}>
            <Card sx={{ ...cardSx, borderTop: `3px solid ${color}` }}>
              <CardContent>
                <Typography
                  sx={{
                    color: color,
                    fontWeight: 700,
                    fontSize: 14,
                    mb: 1,
                    wordBreak: "break-all",
                  }}
                >
                  {k.page}
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                  <KpiItem label="ユーザー" value={k.users.toLocaleString()} />
                  <KpiItem
                    label="最終到達率"
                    value={`${(k.reachRate * 100).toFixed(1)}%`}
                  />
                  <KpiItem
                    label="CTAクリック"
                    value={`${k.ctaTotal.toLocaleString()}件`}
                  />
                  <KpiItem
                    label="CTA到達率"
                    value={`${(k.ctaRate * 100).toFixed(1)}%`}
                  />
                  <KpiItem
                    label="最長滞在"
                    value={k.maxDwell == null ? '未計測' : `${Math.round(k.maxDwell)}秒`}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

const KpiItem = ({ label, value }) => (
  <Box>
    <Typography sx={{ color: theme.subText, fontSize: 11 }}>{label}</Typography>
    <Typography sx={{ color: theme.text, fontSize: 16, fontWeight: 700 }}>
      {value}
    </Typography>
  </Box>
);

// ファネルチャート
const FunnelChart = ({ funnel, selected, metric }) => {
  // section の order 順にキーを並べ、各 LP を Line として描画
  const chartData = useMemo(() => {
    if (selected.length === 0 || funnel.length === 0) return [];
    // 全 LP を通じて出現する section（order でグルーピング）
    const allSections = {};
    funnel.forEach((r) => {
      if (!selected.includes(r.page)) return;
      const key = `${r.order}:${r.section}`;
      if (!allSections[key]) {
        allSections[key] = { order: r.order, section: r.section, label: `${r.order}. ${r.section}` };
      }
    });
    const sortedKeys = Object.values(allSections).sort((a, b) => a.order - b.order);

    // LP→section→値 の辞書
    const lookup = {};
    funnel.forEach((r) => {
      if (!lookup[r.page]) lookup[r.page] = {};
      lookup[r.page][`${r.order}:${r.section}`] = r[metric] || 0;
    });

    // first section value (per LP) を基準に到達率も持たせる
    const firsts = {};
    selected.forEach((page) => {
      const firstKey = sortedKeys[0]?.order != null ? `${sortedKeys[0].order}:${sortedKeys[0].section}` : null;
      firsts[page] = (firstKey && lookup[page]?.[firstKey]) || 0;
    });

    return sortedKeys.map((s) => {
      const row = { label: s.label };
      selected.forEach((page) => {
        row[page] = lookup[page]?.[`${s.order}:${s.section}`] || 0;
      });
      return row;
    });
  }, [funnel, selected, metric]);

  if (chartData.length === 0) {
    return (
      <Card sx={{ ...cardSx, mb: 3 }}>
        <CardContent>
          <Typography sx={{ color: theme.subText, fontSize: 13 }}>
            データなし
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ ...cardSx, mb: 3 }}>
      <CardContent>
        <Typography sx={{ color: theme.text, fontWeight: 700, mb: 2 }}>
          セクション通過ファネル{" "}
          <span style={{ color: theme.subText, fontSize: 12, fontWeight: 400 }}>
            （縦軸: {metric === "total_users" ? "ユニークユーザー" : "イベント数"}）
          </span>
        </Typography>
        <ResponsiveContainer width="100%" height={360}>
          <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 70 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.grid} />
            <XAxis
              dataKey="label"
              tick={{ fill: theme.subText, fontSize: 11 }}
              angle={-30}
              textAnchor="end"
              interval={0}
              height={80}
            />
            <YAxis tick={{ fill: theme.subText, fontSize: 11 }} />
            <Tooltip
              contentStyle={{
                background: theme.tooltip,
                border: `1px solid ${theme.border}`,
                borderRadius: 8,
                color: theme.text,
              }}
            />
            <Legend wrapperStyle={{ color: theme.subText, fontSize: 12 }} />
            {selected.map((page, i) => (
              <Line
                key={page}
                type="monotone"
                dataKey={page}
                stroke={LP_COLORS[i % LP_COLORS.length]}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

// 滞在ヒートマップ（縦=section, 横=LP）
const DwellHeatmap = ({ dwell, selected }) => {
  // section の order 順にユニークなセクション列を抽出
  const { sections, valueMatrix, maxVal } = useMemo(() => {
    const sec = {};
    dwell.forEach((r) => {
      if (!selected.includes(r.page)) return;
      const key = `${r.order}:${r.section}`;
      if (!sec[key]) sec[key] = { order: r.order, section: r.section };
    });
    const sortedSections = Object.values(sec).sort((a, b) => a.order - b.order);

    const lookup = {};
    let mx = 0;
    dwell.forEach((r) => {
      const k = `${r.page}|${r.order}:${r.section}`;
      lookup[k] = r.avg_dwell_seconds;
      if (r.avg_dwell_seconds > mx) mx = r.avg_dwell_seconds;
    });
    return { sections: sortedSections, valueMatrix: lookup, maxVal: mx };
  }, [dwell, selected]);

  if (sections.length === 0 || selected.length === 0) return null;

  return (
    <Card sx={{ ...cardSx, mb: 3 }}>
      <CardContent>
        <Typography sx={{ color: theme.text, fontWeight: 700, mb: 2 }}>
          セクション平均滞在時間ヒートマップ{" "}
          <span style={{ color: theme.subText, fontSize: 12, fontWeight: 400 }}>
            （秒・色濃度=長さ）
          </span>
        </Typography>
        <Box sx={{ overflowX: "auto" }}>
          <Box
            component="table"
            sx={{
              borderCollapse: "separate",
              borderSpacing: 4,
              minWidth: 480,
            }}
          >
            <thead>
              <tr>
                <th style={{ textAlign: "left", color: theme.subText, fontSize: 12, padding: "4px 8px" }}>
                  セクション
                </th>
                {selected.map((p, i) => (
                  <th
                    key={p}
                    style={{
                      color: LP_COLORS[i % LP_COLORS.length],
                      fontSize: 12,
                      padding: "4px 8px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {p}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sections.map((s) => (
                <tr key={`${s.order}:${s.section}`}>
                  <td
                    style={{
                      color: theme.text,
                      fontSize: 12,
                      padding: "6px 8px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {s.order}. {s.section}
                  </td>
                  {selected.map((p) => {
                    const v = valueMatrix[`${p}|${s.order}:${s.section}`] || 0;
                    return (
                      <td
                        key={`${p}|${s.order}:${s.section}`}
                        style={{
                          background: heatColor(v, maxVal),
                          color: theme.text,
                          fontSize: 12,
                          padding: "6px 10px",
                          textAlign: "right",
                          borderRadius: 4,
                          minWidth: 70,
                        }}
                      >
                        {v ? v.toFixed(1) : "—"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// イベント集計テーブル（cta_id / qa_id / method × LP）
const EventBreakdownTable = ({ title, rows, selected, valueKey = "event_count" }) => {
  const { keys, matrix } = useMemo(() => {
    const ks = new Set();
    const m = {};
    rows.forEach((r) => {
      if (!selected.includes(r.page)) return;
      const k = r.key || "(unspecified)";
      ks.add(k);
      if (!m[k]) m[k] = {};
      m[k][r.page] = (m[k][r.page] || 0) + r[valueKey];
    });
    return { keys: Array.from(ks).sort(), matrix: m };
  }, [rows, selected, valueKey]);

  if (keys.length === 0) {
    return (
      <Card sx={{ ...cardSx, mb: 2 }}>
        <CardContent>
          <Typography sx={{ color: theme.text, fontWeight: 700, mb: 1 }}>{title}</Typography>
          <Typography sx={{ color: theme.subText, fontSize: 12 }}>データなし</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ ...cardSx, mb: 2 }}>
      <CardContent>
        <Typography sx={{ color: theme.text, fontWeight: 700, mb: 2 }}>{title}</Typography>
        <Box sx={{ overflowX: "auto" }}>
          <Box component="table" sx={{ borderCollapse: "collapse", width: "100%", minWidth: 360 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                <th style={{ textAlign: "left", color: theme.subText, fontSize: 12, padding: "6px 8px" }}>
                  キー
                </th>
                {selected.map((p, i) => (
                  <th
                    key={p}
                    style={{
                      textAlign: "right",
                      color: LP_COLORS[i % LP_COLORS.length],
                      fontSize: 12,
                      padding: "6px 8px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {p}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k} style={{ borderBottom: `1px solid ${theme.grid}` }}>
                  <td style={{ color: theme.text, fontSize: 13, padding: "6px 8px" }}>{k}</td>
                  {selected.map((p) => (
                    <td
                      key={p}
                      style={{
                        textAlign: "right",
                        color: theme.text,
                        fontSize: 13,
                        padding: "6px 8px",
                      }}
                    >
                      {matrix[k]?.[p]?.toLocaleString() || "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

function LpBehaviorPage() {
  useLoginCheck();
  const apiUrl = process.env.REACT_APP_API_URL;

  // 期間
  const [preset, setPreset] = useState("28");
  const [start, setStart] = useState(daysAgo(28));
  const [end, setEnd] = useState(daysAgo(0));

  // プリセット → 日付
  useEffect(() => {
    if (preset === "custom") return;
    const p = PRESETS.find((x) => x.key === preset);
    if (p && p.days != null) {
      setStart(daysAgo(p.days));
      setEnd(daysAgo(0));
    }
  }, [preset]);

  // LP 一覧
  const [pages, setPages] = useState([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [selected, setSelected] = useState([]);

  // データ
  const [funnel, setFunnel] = useState([]);
  const [dwell, setDwell] = useState([]);
  const [aggregates, setAggregates] = useState({
    cta_click: [],
    qa_open: [],
    flow_navigate: [],
    instructor_more: [],
  });
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState("");

  // 表示メトリクス（ファネル）
  const [funnelMetric, setFunnelMetric] = useState("total_users");

  // LP 一覧の取得
  useEffect(() => {
    if (!start || !end) return;
    const fetchPages = async () => {
      setPagesLoading(true);
      setError("");
      try {
        const url = `${apiUrl}/api/lp_behavior/pages/?start=${start}&end=${end}`;
        const res = await fetch(url, {credentials:"include",cache:"no-store"});
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
        const ps = json.pages || [];
        setPages(ps);
        // デフォルトで上位2件を選択
        setSelected((cur) => {
          if (cur.length > 0) {
            // 期間変更時に存在しない page が残っていれば除外
            const stillValid = cur.filter((p) => ps.some((x) => x.page === p));
            if (stillValid.length > 0) return stillValid;
          }
          return ps.slice(0, 2).map((p) => p.page);
        });
      } catch (e) {
        setPages([]);setSelected([]);setFunnel([]);setDwell([]);setAggregates({cta_click:[],qa_open:[],flow_navigate:[],instructor_more:[]});
        setError(`LP一覧の取得に失敗: ${e.message}`);
      } finally {
        setPagesLoading(false);
      }
    };
    fetchPages();
  }, [apiUrl, start, end]);

  // 比較データ取得
  const fetchCompare = useCallback(async () => {
    if (selected.length === 0 || !start || !end) return;
    setDataLoading(true);
    setError("");
    try {
      const pagesParam = encodeURIComponent(selected.join(","));
      const [funnelRes, aggRes] = await Promise.all([
        fetch(`${apiUrl}/api/lp_behavior/funnel/?pages=${pagesParam}&start=${start}&end=${end}`, {credentials:"include",cache:"no-store"}),
        fetch(`${apiUrl}/api/lp_behavior/aggregates/?pages=${pagesParam}&start=${start}&end=${end}`, {credentials:"include",cache:"no-store"}),
      ]);
      const fj = await funnelRes.json();
      const aj = await aggRes.json();
      if (!funnelRes.ok) throw new Error(fj.error || `funnel HTTP ${funnelRes.status}`);
      if (!aggRes.ok) throw new Error(aj.error || `aggregates HTTP ${aggRes.status}`);
      setFunnel(fj.funnel || []);
      setDwell(fj.dwell || []);
      setAggregates({
        cta_click: aj.cta_click || [],
        qa_open: aj.qa_open || [],
        flow_navigate: aj.flow_navigate || [],
        instructor_more: aj.instructor_more || [],
      });
    } catch (e) {
      setFunnel([]);setDwell([]);setAggregates({cta_click:[],qa_open:[],flow_navigate:[],instructor_more:[]});
      setError(`データ取得に失敗: ${e.message}`);
    } finally {
      setDataLoading(false);
    }
  }, [apiUrl, selected, start, end]);

  useEffect(() => {
    fetchCompare();
  }, [fetchCompare]);

  const toggleLp = (page) => {
    setSelected((cur) =>
      cur.includes(page) ? cur.filter((p) => p !== page) : [...cur, page]
    );
  };

  return (
    <Box sx={{ bgcolor: theme.background, minHeight: "100vh", p: { xs: 2, md: 3 } }}>
<BehaviorScope scope="overall"/>      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ color: theme.text }}>
          LP行動分析
        </Typography>
        <Typography sx={{ color: theme.subText, mt: 0.5, fontSize: 14 }}>
          GA4イベント（section_view / section_dwell / cta_click / qa_open / flow_navigate / instructor_more）でLPを横並び比較
        </Typography>
      </Box>

      <PeriodPicker
        preset={preset}
        setPreset={setPreset}
        start={start}
        end={end}
        setStart={setStart}
        setEnd={setEnd}
      />

      <LpPicker
        available={pages}
        selected={selected}
        toggle={toggleLp}
        loading={pagesLoading}
      />

      {error && (
        <Card sx={{ ...cardSx, mb: 2, borderLeft: "3px solid #f87171" }}>
          <CardContent>
            <Typography sx={{ color: "#f87171", fontSize: 13 }}>{error}</Typography>
          </CardContent>
        </Card>
      )}

      {dataLoading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <CircularProgress size={16} sx={{ color: theme.subText }} />
          <Typography sx={{ color: theme.subText, fontSize: 13 }}>取得中...</Typography>
        </Box>
      )}

      {selected.length === 0 ? (
        <Card sx={cardSx}>
          <CardContent>
            <Typography sx={{ color: theme.subText, fontSize: 14 }}>
              比較するLPを上のチェックボックスから1つ以上選択してください
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <KpiCards
            pages={pages}
            selected={selected}
            funnel={funnel}
            dwell={dwell}
            cta={aggregates.cta_click}
            aggregates={aggregates}
          />

          <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
            <Button
              size="small"
              onClick={() => setFunnelMetric("total_users")}
              sx={{
                bgcolor: funnelMetric === "total_users" ? LP_COLORS[0] : theme.border,
                color: funnelMetric === "total_users" ? "#000" : theme.subText,
                fontWeight: 700,
                fontSize: 12,
                "&:hover": { opacity: 0.85, bgcolor: funnelMetric === "total_users" ? LP_COLORS[0] : theme.border },
              }}
            >
              ユニークユーザー
            </Button>
            <Button
              size="small"
              onClick={() => setFunnelMetric("event_count")}
              sx={{
                bgcolor: funnelMetric === "event_count" ? LP_COLORS[0] : theme.border,
                color: funnelMetric === "event_count" ? "#000" : theme.subText,
                fontWeight: 700,
                fontSize: 12,
                "&:hover": { opacity: 0.85, bgcolor: funnelMetric === "event_count" ? LP_COLORS[0] : theme.border },
              }}
            >
              イベント数
            </Button>
          </Box>

          <FunnelChart funnel={funnel} selected={selected} metric={funnelMetric} />

          <DwellHeatmap dwell={dwell} selected={selected} />

          <EventBreakdownTable
            title="CTAクリック (cta_id 別)"
            rows={aggregates.cta_click}
            selected={selected}
          />
          <EventBreakdownTable
            title="QA開封 (qa_id 別)"
            rows={aggregates.qa_open}
            selected={selected}
          />
          <EventBreakdownTable
            title="フロー操作 (method 別: dot / step_label)"
            rows={aggregates.flow_navigate}
            selected={selected}
          />

          {/* instructor_more は LP ごと総数のみ */}
          <Card sx={cardSx}>
            <CardContent>
              <Typography sx={{ color: theme.text, fontWeight: 700, mb: 2 }}>
                インストラクターMORE クリック
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                {selected.map((p, i) => {
                  const row = aggregates.instructor_more.find((r) => r.page === p);
                  return (
                    <Box key={p}>
                      <Typography sx={{ color: LP_COLORS[i % LP_COLORS.length], fontSize: 12 }}>
                        {p}
                      </Typography>
                      <Typography sx={{ color: theme.text, fontSize: 20, fontWeight: 700 }}>
                        {row ? row.event_count.toLocaleString() : 0}
                        <span style={{ color: theme.subText, fontSize: 12, marginLeft: 4 }}>
                          件 ({row ? row.total_users : 0} ユーザー)
                        </span>
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}

export default LpBehaviorPage;
